<template>
  <div class="shared-link-list">
    <div class="title-wrapper">
      <span class="arrow" @click="back">＜</span>
      <span class="title">{{ sharedLinkName }}</span>
    </div>
    <div class="list" v-if="kind === SharedLinkKind.MANUSCRIPT">
      <transition-group name="tran" v-if="manuscriptList.length">
        <div v-for="item in manuscriptList" :key="item.key" class="item" @click="select(item.key)">
          <div class="item-title ellipsis">
            {{ item.title || $t("sharedLinkContentListOnSlideMenu.untitledItem") }}
          </div>
          <dot-icon v-if="isIncludesUnreadComment(item.key)" class="notification" />
        </div>
      </transition-group>
      <div v-else class="description">
        <div>{{ $t("sharedLinkContentListOnSlideMenu.noManuscripts") }}</div>
      </div>
    </div>
    <div class="list" v-if="kind === SharedLinkKind.PLOT">
      <transition-group name="tran" v-if="plotList.length">
        <div v-for="item in plotList" :key="item.plotId" class="item" @click="select(item.plotId)">
          <div class="item-title ellipsis">
            {{ item.name || $t("sharedLinkContentListOnSlideMenu.untitledItem") }}
          </div>
          <dot-icon v-if="isIncludesUnreadComment(item.plotId)" class="notification" />
        </div>
      </transition-group>
      <div v-else class="description">
        <div>{{ $t("sharedLinkContentListOnSlideMenu.noPlots") }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { SharedLink, Manuscript, User, SharedLinkKind, SubPlot, PlotList } from "@/lib/models";
import { Comment as CommentDexie } from "@/lib/indexeddb";
import DotIcon from "@/components/atoms/DotIcon.vue";

export default Vue.extend<Data, Methods, Computed, Props>({
  data() {
    return {
      SharedLinkKind,
    };
  },
  components: { DotIcon },
  props: {
    novelId: {
      type: String as PropType<string>,
    },
    kind: {
      type: String,
      required: true,
    },
  },
  methods: {
    async select(contentKey) {
      this.$store.dispatch("sharedLinkModule/selectContent", contentKey);
    },
    back() {
      this.$store.dispatch("sharedLinkModule/initializeSelectedSharedLink");
    },
  },
  computed: {
    user() {
      return this.$store.getters["userModule/user"] as User;
    },
    selectedSharedLinkId() {
      return this.$store.getters["sharedLinkModule/selectedSharedLinkId"];
    },
    sharedLinkName() {
      const sharedLinkList = this.$store.getters["sharedLinkModule/sharedLinkList"] as SharedLink[];
      const sharedLink = sharedLinkList.find((sharedLink) => sharedLink.sharedLinkId === this.selectedSharedLinkId);
      return sharedLink!.name || "NO TITLE";
    },
    manuscriptList() {
      const sharedLinkList = this.$store.getters["sharedLinkModule/sharedLinkList"] as SharedLink[];
      const sharedLink: SharedLink = sharedLinkList.find(
        (sharedLink) => sharedLink.sharedLinkId === this.selectedSharedLinkId
      )!;
      const manuscriptList = this.$store.getters["manuscriptModule/manuscriptList"] as Manuscript[];
      return manuscriptList.filter((manuscript) => sharedLink.manuscriptKeys!.includes(manuscript.key!));
    },
    plotList() {
      const sharedLinkList = this.$store.getters["sharedLinkModule/sharedLinkList"] as SharedLink[];
      const sharedLink: SharedLink = sharedLinkList.find(
        (sharedLink) => sharedLink.sharedLinkId === this.selectedSharedLinkId
      )!;

      const plots = this.$store.getters["plotModule/plots"];
      const overallPlot = {
        name: this.$t("sharedLinkContentListOnSlideMenu.overallPlot") as string,
        plotId: `overall-plot-${sharedLink.novelId}`,
        ...plots,
      };

      const subPlots = this.$store.getters["plotModule/subPlots"];

      if (sharedLink && sharedLink.plotIds) {
        return sharedLink.plotIds
          .map((plotId) => {
            if (plotId === overallPlot.plotId) {
              return overallPlot;
            }
            return subPlots.find((subPlot: SubPlot) => subPlot.plotId === plotId);
          })
          .filter((plot) => plot !== undefined);
      }
      return [];
    },
    isIncludesUnreadComment() {
      return (manuscriptKey) => {
        const { novelId, kind } = this;
        const { userId } = this.user as User;
        const sharedLinkId = this.selectedSharedLinkId;
        const unreadCommentList = this.$store.getters["sharedLinkModule/unreadCommentList"](
          userId,
          novelId,
          kind,
          sharedLinkId,
          manuscriptKey
        ) as CommentDexie[];
        return !!unreadCommentList.length;
      };
    },
  },
});

interface Data {
  SharedLinkKind: typeof SharedLinkKind;
}

interface Props {
  novelId: string;
  kind: string;
}

interface Methods {
  select: (sharedLinkId: string) => void;
  back: () => void;
}

interface Computed {
  user: User;
  selectedSharedLinkId: string;
  sharedLinkName: string;
  manuscriptList: Manuscript[];
  plotList: PlotList | [];
  isIncludesUnreadComment: (manuscriptKey: string) => boolean;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
@mixin link {
  background: transparent;
  border: none;
  cursor: pointer;
}

.shared-link-list {
  width: 100%;
  padding-left: 16px;
  padding-right: 4px;
  border: 1px solid var(--color-border);
  border-top-left-radius: 10px;
  background-color: var(--color-background-normal);

  height: 100%;
  display: flex;
  flex-direction: column;

  .title-wrapper {
    display: flex;
    align-items: center;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    margin: 12px 0;
    color: var(--color-text-normal);

    .arrow {
      margin-right: 10px;
      cursor: pointer;

      &:hover {
        color: var(--color-primary);
      }
    }

    .title {
      user-select: none;
    }
  }
}

.list {
  flex-grow: 1;
  overflow-y: auto;

  .item {
    box-sizing: border-box;
    height: 50px;
    display: flex;
    align-items: center;
    padding: 0 15px;
    cursor: pointer;
    border-bottom: 1px solid var(--color-border);
    color: var(--color-text-normal);
    position: relative;
    @include hover-overlay;

    &.active {
      background-color: var(--color-surface-subtle);

      &::after {
        display: none;
      }
    }

    .item-title {
      width: 250px;
      font-size: 1.1em;
    }

    .notification {
      fill: var(--color-warning);
      width: 1em;
      height: 1em;
    }
  }

  .description {
    margin: 20px 15px;
    padding: 20px 10px;
    color: var(--color-text-low);
  }
}
</style>
